'use client';

import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';

interface AuthBoundaryProps {
  children: React.ReactNode;
  requiredPermission?: string;
}

export const AuthBoundary: React.FC<AuthBoundaryProps> = ({
  children,
  requiredPermission,
}) => {
  const { isAuthenticated, isLoading, user, hasPermission, loginWithPlaceholder } = useAuth();
  const [selectedRole, setSelectedRole] = useState<'superadmin' | 'pm' | 'auditor' | 'readonly'>('pm');
  const [customActor, setCustomActor] = useState('ops-admin@imarket.local');
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400 font-mono text-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <span>Validating admin credentials...</span>
          </div>
          <noscript>
            <p className="text-amber-400 text-xs mt-2">JavaScript is required to sign in to iMarket Admin.</p>
          </noscript>
        </div>
      </div>
    );
  }

  // Not authenticated: Show login / auth boundary placeholder
  if (!isAuthenticated || !user) {
    const handleLogin = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      try {
        await loginWithPlaceholder(customActor, selectedRole);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm bg-zinc-900/90 border border-zinc-800 rounded-lg shadow-2xl p-6">
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" />
                <h1 className="text-base font-semibold text-zinc-100 tracking-tight">iMarket Admin</h1>
              </div>
              <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-zinc-800 text-amber-400 border border-zinc-700/60">
                Development Mode
              </span>
            </div>
            <p className="text-xs text-zinc-400">Sign in to access administrative operations</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1.5">Admin Actor</label>
              <input
                type="text"
                value={customActor}
                onChange={(e) => setCustomActor(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 rounded px-3 py-2 text-xs font-mono text-zinc-100 focus:outline-none transition-colors"
                placeholder="ops-admin@imarket.local"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1.5">Permission Preset</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as any)}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 rounded px-3 py-2 text-xs text-zinc-200 focus:outline-none transition-colors"
              >
                <option value="pm">Product Manager (PM: Full Reads + Credit Grant)</option>
                <option value="superadmin">SuperAdmin (Full Permissions)</option>
                <option value="auditor">Auditor (Audit & Health Only)</option>
                <option value="readonly">ReadOnly Operator</option>
              </select>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold py-2 px-4 rounded text-xs transition-colors disabled:opacity-50"
              >
                {submitting ? 'Connecting...' : 'Enter Admin Console'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Authenticated, check permission requirement if specified
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="p-8 max-w-lg mx-auto mt-12 bg-zinc-900 border border-rose-900/60 rounded-lg">
        <h2 className="text-base font-semibold text-rose-400 mb-2">Access Denied (Deny By Default)</h2>
        <p className="text-xs text-zinc-400 font-mono mb-4">
          Your current administrative session lacks the required permission:
        </p>
        <div className="bg-zinc-950 p-2.5 rounded font-mono text-xs text-rose-300 border border-zinc-800 mb-4">
          {requiredPermission}
        </div>
        <p className="text-xs text-zinc-500">
          Contact a SuperAdmin to grant access. This action has been logged in the audit trail.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

